/**
 * Whole nights between two ISO dates, or 0 when either is missing or invalid.
 *
 * Shared by the sidebar and the checkout summary so the two cannot disagree
 * about the length of the same stay.
 */
export function nightsBetween(checkin?: string, checkout?: string): number {
  if (!checkin || !checkout) return 0;
  const from = new Date(`${checkin}T00:00:00`).getTime();
  const to = new Date(`${checkout}T00:00:00`).getTime();
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return 0;
  return Math.round((to - from) / 86_400_000);
}

/**
 * Ages arrive in the URL as "2,15". Anything unparseable is dropped rather
 * than guessed: a wrong age is a wrong price, and the supplier charges on it.
 *
 * Empty segments are dropped BEFORE the numbers are parsed. Without that,
 * an absent param produced [0]: `String(undefined ?? "")` is `""`, which
 * splits to `[""]`, and `Number("")` is 0, a valid age. So every adults-only
 * page quietly searched with a phantom newborn. Measured against the live
 * supplier on 2026-08-13: La Mamounia, 2 adults, 2 nights came back at
 * 19,773.22 MAD with no children and 41,254.52 MAD with the phantom, and the
 * sidebar told the guest they had booked for "2 adults, 1 child".
 */
export function parseChildAges(raw: string | number | undefined): number[] {
  // A single age can reach us as a number ("childAges=5"), so this coerces
  // rather than assuming the router handed over a string.
  return String(raw ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "")
    .map(Number)
    .filter((n) => Number.isInteger(n) && n >= 0);
}

/**
 * "2 adults, 1 child". One definition, used by the sidebar, the checkout, the
 * confirmation and the manage page, because a stay is priced on the split and
 * a single "3 guests" hides the fact that a child was booked at all.
 */
export function formatGuestParty(
  t: (key: string) => string,
  adults: number,
  childCount: number,
): string {
  const parts = [`${adults} ${adults === 1 ? t("stays.adult_singular") : t("stays.adults_plural")}`];
  if (childCount > 0) {
    parts.push(
      `${childCount} ${childCount === 1 ? t("stays.child_singular") : t("stays.children_plural")}`,
    );
  }
  return parts.join(", ");
}
